import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Crown } from 'lucide-react';

function AdvancedMenuRow({ icon: Icon, label, title, onClick, disabled, showCrown, danger }) {
  return (
    <button
      type="button"
      disabled={disabled}
      title={title}
      onClick={onClick}
      className={`w-full flex items-center gap-2.5 px-3 py-2 text-left text-xs font-medium rounded-lg transition-colors ${
        disabled
          ? 'opacity-40 cursor-not-allowed text-slate-400'
          : danger
            ? 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
            : 'text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800/80'
      }`}
    >
      {Icon ? <Icon size={15} className="shrink-0 opacity-80" /> : null}
      <span className="flex-1">{label}</span>
      {showCrown ? <Crown size={11} className="text-amber-400 shrink-0" /> : null}
    </button>
  );
}

function useHoverCapablePointer() {
  const [hoverCapable, setHoverCapable] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  });

  useEffect(() => {
    const mq = window.matchMedia('(hover: hover) and (pointer: fine)');
    const update = () => setHoverCapable(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  return hoverCapable;
}

export default function TopbarAdvancedMenu({ items = [] }) {
  const { t } = useTranslation(['builder']);
  const hoverCapable = useHoverCapablePointer();
  const [open, setOpen] = useState(false);
  const [flyoutPos, setFlyoutPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const flyoutRef = useRef(null);
  const closeTimerRef = useRef(null);
  const openedAtRef = useRef(0);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const markOpened = useCallback(() => {
    openedAtRef.current = Date.now();
  }, []);

  const scheduleClose = useCallback(() => {
    if (!hoverCapable) return;
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => setOpen(false), 180);
  }, [clearCloseTimer, hoverCapable]);

  const updateFlyoutPosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const panelWidth = 208;
    let left = rect.right - panelWidth;
    left = Math.max(8, Math.min(left, window.innerWidth - panelWidth - 8));
    setFlyoutPos({
      top: rect.bottom + 6,
      left,
    });
  }, []);

  const openMenu = useCallback(() => {
    clearCloseTimer();
    updateFlyoutPosition();
    markOpened();
    setOpen(true);
  }, [clearCloseTimer, updateFlyoutPosition, markOpened]);

  const closeMenu = useCallback(() => {
    clearCloseTimer();
    setOpen(false);
  }, [clearCloseTimer]);

  const handleEnter = useCallback(() => {
    if (!hoverCapable) return;
    openMenu();
  }, [hoverCapable, openMenu]);

  useEffect(() => () => clearCloseTimer(), [clearCloseTimer]);

  useEffect(() => {
    if (!open) return undefined;
    updateFlyoutPosition();
    const onReflow = () => updateFlyoutPosition();
    window.addEventListener('resize', onReflow);
    window.addEventListener('scroll', onReflow, true);
    return () => {
      window.removeEventListener('resize', onReflow);
      window.removeEventListener('scroll', onReflow, true);
    };
  }, [open, updateFlyoutPosition]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocPointer = (e) => {
      if (Date.now() - openedAtRef.current < 320) return;
      if (triggerRef.current?.contains(e.target)) return;
      if (flyoutRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', onDocPointer);
    return () => document.removeEventListener('pointerdown', onDocPointer);
  }, [open]);

  const handleTriggerClick = useCallback(
    (e) => {
      e.stopPropagation();
      updateFlyoutPosition();
      if (open) {
        closeMenu();
      } else {
        openMenu();
      }
    },
    [open, updateFlyoutPosition, closeMenu, openMenu]
  );

  if (!items.length) return null;

  const flyoutPortal = open
    ? createPortal(
        <>
          {!hoverCapable ? (
            <button
              type="button"
              className="fixed inset-0 z-[100050] bg-slate-900/35 border-0 p-0 cursor-pointer"
              aria-label={t('builder:close', { defaultValue: 'Fechar' })}
              onClick={closeMenu}
            />
          ) : null}
          <div
            ref={flyoutRef}
            className="fixed z-[100060]"
            style={{ top: flyoutPos.top, left: flyoutPos.left }}
            onMouseEnter={hoverCapable ? handleEnter : undefined}
            onMouseLeave={hoverCapable ? scheduleClose : undefined}
          >
            <div
              className="w-52 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-xl shadow-slate-900/10 dark:shadow-black/40 py-1.5 animate-in fade-in slide-in-from-top-1 duration-150"
              role="menu"
            >
              <div className="px-3 py-1.5 border-b border-slate-100 dark:border-slate-800 mb-0.5">
                <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">
                  {t('builder:topbarAdvancedTitle', { defaultValue: 'Avançado' })}
                </p>
              </div>
              <div className="px-1.5 space-y-0.5">
                {items.map((item) => (
                  <AdvancedMenuRow
                    key={item.id}
                    icon={item.icon}
                    label={item.label}
                    title={item.title}
                    disabled={item.disabled}
                    showCrown={item.showCrown}
                    danger={item.id === 'logout' || item.id === 'reset'}
                    onClick={() => {
                      if (item.disabled) return;
                      item.onClick?.();
                      closeMenu();
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        </>,
        document.body
      )
    : null;

  return (
    <>
      <span className="w-px h-6 bg-slate-200 dark:bg-slate-600 mx-1" />
      <div
        className="relative"
        onMouseEnter={hoverCapable ? handleEnter : undefined}
        onMouseLeave={hoverCapable ? scheduleClose : undefined}
      >
        <button
          ref={triggerRef}
          type="button"
          onClick={handleTriggerClick}
          aria-expanded={open}
          aria-haspopup="true"
          className={`flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors ${
            open
              ? 'text-slate-800 dark:text-slate-100 bg-slate-100 dark:bg-slate-700'
              : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
          }`}
        >
          <span>{t('builder:topbarAdvanced', { defaultValue: 'Avançado' })}</span>
          <ChevronDown size={14} className={`shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </div>
      {flyoutPortal}
    </>
  );
}
